/**
 * ContinueExploring — universal "what to look at next" panel.
 *
 * Pulls connected items from the TCP graph and groups them by kind. Lives
 * at the bottom of every research detail page (vaccine, ingredient,
 * disease, manufacturer, surveillance, topic, source...).
 *
 * Reusable across explorers — works for any object that has a list of
 * connected items: each section is rendered as a card grid with kind
 * badges + SaveButtons.
 *
 * Props
 *   items   Array of { kind, slug, title, summary?, badge?, href? }
 *           Already enriched server-side — this component is purely
 *           presentational.
 *   parentRef  Identifier of the page this lives on (e.g. "vaccine:mmr"),
 *              passed through to SaveButton for research-trail grouping.
 */
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SaveButton from "./SaveButton";

const KIND_LABELS = {
    topic: "Investigation",
    source: "Source document",
    event: "Timeline event",
    entity: "Entity",
    vaccine: "Vaccine",
    ingredient: "Ingredient",
    disease: "Disease",
    manufacturer: "Manufacturer",
    surveillance_system: "Surveillance system",
    evidence_block: "Evidence section",
    research_area: "Research area",
};

function defaultHref(item) {
    if (item.href) return item.href;
    switch (item.kind) {
        case "topic":            return `/topic/${item.slug}`;
        case "source":           return `/source/${item.slug}`;
        case "entity":           return `/entity/${item.slug}`;
        case "event":            return `/timeline#${item.slug}`;
        case "vaccine":          return `/vaccines/v/${item.slug}`;
        case "ingredient":
        case "disease":
        case "manufacturer":
        case "surveillance_system":
            return `/entity/${item.slug}`;
        default:                 return "#";
    }
}

export default function ContinueExploring({ items = [], parentRef = null, className = "" }) {
    const grouped = useMemo(() => {
        const out = {};
        for (const item of items) {
            if (!item || !item.kind) continue;
            (out[item.kind] = out[item.kind] || []).push(item);
        }
        return out;
    }, [items]);

    const kinds = Object.keys(grouped);
    if (kinds.length === 0) return null;

    return (
        <section
            id="evidence-context"
            className={`bg-[#0B1220] border-t border-[#243044] py-12 ${className}`}
            data-testid="continue-exploring"
        >
            <div className="max-w-6xl mx-auto px-6">
                <div className="mb-6">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#56CCF2] font-semibold mb-1.5">Continue exploring</div>
                    <h2 className="text-2xl font-bold text-white tracking-tight">Where to go next</h2>
                </div>
                <div className="space-y-6">
                    {kinds.map((kind) => (
                        <div key={kind}>
                            <h3 className="text-[11px] uppercase tracking-[0.22em] text-[#94A3B8] mb-2.5">{KIND_LABELS[kind] || kind}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {grouped[kind].map((item) => (
                                    <div
                                        key={`${item.kind}-${item.slug}`}
                                        className="group relative bg-[#111827] border border-[#243044] rounded-lg p-4 hover:border-[#56CCF2]/50 transition-colors"
                                        data-testid={`continue-card-${item.kind}-${item.slug}`}
                                    >
                                        <Link to={defaultHref(item)} className="block">
                                            <div className="flex items-start justify-between gap-2 mb-1">
                                                {item.badge ? (
                                                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#243044] text-[#94A3B8]">{item.badge}</span>
                                                ) : <span />}
                                                <ArrowUpRight className="w-3.5 h-3.5 text-[#64748B] group-hover:text-[#56CCF2] transition-colors" />
                                            </div>
                                            <h4 className="text-sm font-semibold text-white leading-snug mb-1.5 group-hover:text-[#56CCF2] transition-colors">
                                                {item.title}
                                            </h4>
                                            {item.summary ? (
                                                <p className="text-[12px] text-[#94A3B8] leading-relaxed line-clamp-2">{item.summary}</p>
                                            ) : null}
                                        </Link>
                                        <div className="absolute top-2 right-2">
                                            <SaveButton
                                                kind={item.kind}
                                                refSlug={item.slug}
                                                refTitle={item.title}
                                                parentRef={parentRef}
                                                refUrl={defaultHref(item)}
                                                variant="icon"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
